// frontend/src/components/litters/WeaningConfirm.js
// The "wean pups from this litter" overlay.
//
// Pups arrive pre-grouped by the parent (autoGroupedPups) — a group is a set
// of pups sharing sex and genotype, so a whole group can be assigned to one
// cage in a single click. Groups expand to individual pups for finer control.
//
// Presentation only: all state and handlers live in LitterManagement. The
// mobile list and bulk-assign controls are passed as render functions rather
// than elements so the desktop path never builds the mobile tree.

import React from 'react';

function WeaningConfirm({
  weaningLitter,
  isMobile,
  overlayRef,
  // pup grouping
  autoGroupedPups,
  expandedGroups,
  selectedGroups,
  pupsSelectedForWeaning,
  pupCageAssignments,
  cages,
  onToggleGroup,
  onToggleGroupExpansion,
  onTogglePup,
  formatGenotype,
  // parent-owned sub-renderers
  renderMobileList,
  renderBulkAssignControls,
  // submit / cancel
  onConfirm,
  isSubmitting,
  onCancel,
  // feedback
  error,
}) {
  const assignedCount = Object.keys(pupCageAssignments).length;
  const cageFor = (pupId) => cages.find((c) => c.id == pupCageAssignments[pupId]);

  return (
    <div ref={overlayRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', padding: isMobile ? '15px' : '30px', borderRadius: '8px', maxWidth: isMobile ? '100%' : '1000px', width: isMobile ? '100%' : '90%', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', ...(isMobile && { borderRadius: 0, height: '100%', maxHeight: '100%', paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))', paddingBottom: 'calc(3.5rem + env(safe-area-inset-bottom, 0px))' }) }}>
        <h3>Wean Pups from Litter: {weaningLitter.litter_number}</h3>
        <p>Select pups and assign them to a cage. You can assign different groups to different cages before confirming.</p>
        {error && <div className="error">{error}</div>}
        <div className="litter-list-container" style={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
          {isMobile ? (
            renderMobileList()
          ) : (
            <table className="litter-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Pup #</th>
                  <th>Physical Tag</th>
                  <th>Sex</th>
                  <th>Genotype</th>
                  <th>Assigned Cage</th>
                </tr>
              </thead>
              <tbody>
                {autoGroupedPups.map((group) => {
                  const isExpanded = expandedGroups[group.id];
                  const isSelected = selectedGroups.includes(group.id);
                  const groupHasAssignments = group.pups.some((pup) => pupCageAssignments[pup.id]);
                  const assignedCage = groupHasAssignments ? cageFor(group.pups[0].id) : null;

                  return (
                    <React.Fragment key={group.id}>
                      {/* Group Header Row */}
                      <tr
                        style={{
                          backgroundColor: isSelected ? '#e7f3ff' : '#f8f9fa',
                          borderTop: '2px solid #28a745',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                        }}
                        onClick={() => onToggleGroup(group.id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => onToggleGroup(group.id)} />
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleGroupExpansion(group.id);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {isExpanded ? '▼' : '▶'} Group
                        </td>
                        <td colSpan="3" style={{ color: '#28a745' }}>
                          {group.label}
                        </td>
                        <td style={{ fontWeight: 'bold', color: assignedCage ? '#28a745' : '#6c757d' }}>
                          {assignedCage ? assignedCage.display_cage_number : 'Not Assigned'}
                        </td>
                      </tr>

                      {/* Individual Pup Rows (when expanded) */}
                      {isExpanded &&
                        group.pups.map((pup) => {
                          const pupAssignedCage = cageFor(pup.id);
                          return (
                            <tr
                              key={pup.id}
                              style={{
                                backgroundColor: pupsSelectedForWeaning.includes(pup.id) ? '#e7f3ff' : 'white',
                                borderLeft: '4px solid #28a745',
                              }}
                              onClick={() => onTogglePup(pup.id)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={pupsSelectedForWeaning.includes(pup.id)}
                                  onChange={() => onTogglePup(pup.id)}
                                />
                              </td>
                              <td style={{ paddingLeft: '20px' }}></td>
                              <td>{pup.physical_tag || 'N/A'}</td>
                              <td>{pup.sex}</td>
                              <td>{formatGenotype(pup.genotype_data)}</td>
                              <td style={{ fontWeight: 'bold', color: pupAssignedCage ? '#28a745' : '#6c757d' }}>
                                {pupAssignedCage ? pupAssignedCage.display_cage_number : 'Not Assigned'}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {renderBulkAssignControls()}
        <div className="modal-actions" style={{ flexShrink: 0, marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid #eee', display: 'flex', gap: '10px' }}>
          <button
            onClick={onConfirm}
            className="primary-button"
            style={{ flex: 1 }}
            disabled={isSubmitting || assignedCount === 0}
          >
            {isSubmitting ? 'Weaning...' : `Confirm Wean (${assignedCount} Pups)`}
          </button>
          <button onClick={onCancel} className="secondary-button" style={{ flex: 1 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeaningConfirm;
